import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";

const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate("/")}
            >
              <img src={BRAIN_LOGO} alt="ABA Prep Lab" className="w-10 h-10 object-contain" />
              <span className="font-bold text-xl text-slate-900 dark:text-white">ABA Prep Lab</span>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-teal-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-8 md:p-12"
        >
          {/* Title */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
            <div className="w-14 h-14 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <Shield className="w-7 h-7 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Privacy Policy</h1>
              <p className="text-slate-500 dark:text-slate-400">Last Updated: January 2026</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-8">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit or use ABA Prep Lab (the "Site"), which provides study guides, quizzes, full-length mock exams, and an ABA dictionary. By using the Site, you agree to the terms of this Privacy Policy.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Personal Information</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-3">We may collect personal information you voluntarily provide, such as:</p>
              <ul className="list-disc pl-6 text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Account login credentials</li>
                <li>Payment information (processed by third-party providers; we do not store full payment details)</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-3">Non-Personal Information</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-3">We automatically collect certain non-identifying information, including:</p>
              <ul className="list-disc pl-6 text-slate-700 dark:text-slate-300 space-y-2">
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Pages visited and usage patterns</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-700 dark:text-slate-300 mb-3">We use collected information to:</p>
              <ul className="list-disc pl-6 text-slate-700 dark:text-slate-300 space-y-2">
                <li>Provide and maintain ABA Prep Lab and its features</li>
                <li>Deliver study content, quizzes, and mock exams</li>
                <li>Improve user experience and site performance</li>
                <li>Communicate updates, support responses, or service notices</li>
                <li>Process transactions and manage subscriptions</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">3. Cookies and Tracking Technologies</h2>
              <p className="text-slate-700 dark:text-slate-300">
                We use cookies and similar technologies to enhance functionality, analyze usage, and improve performance. You can control cookie preferences through your browser settings.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">4. Sharing of Information</h2>
              <p className="text-slate-700 dark:text-slate-300 mb-3">We do not sell your personal information. We may share information with:</p>
              <ul className="list-disc pl-6 text-slate-700 dark:text-slate-300 space-y-2">
                <li>Service providers who help operate ABA Prep Lab</li>
                <li>Payment processors and analytics providers</li>
                <li>Legal authorities when required by law</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">5. Data Security</h2>
              <p className="text-slate-700 dark:text-slate-300">
                We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no method of transmission or storage is completely secure.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">6. Third-Party Links</h2>
              <p className="text-slate-700 dark:text-slate-300">
                ABA Prep Lab may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">7. Children's Privacy</h2>
              <p className="text-slate-700 dark:text-slate-300">
                ABA Prep Lab is intended for users 18 years or older. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">8. Your Rights and Choices</h2>
              <p className="text-slate-700 dark:text-slate-300">
                Depending on your location, you may have rights to access, correct, or delete your personal information. You may also opt out of non-essential communications at any time.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">9. Changes to This Privacy Policy</h2>
              <p className="text-slate-700 dark:text-slate-300">
                We may update this Privacy Policy periodically. Changes will be posted on this page with an updated revision date.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">10. Contact Us</h2>
              <p className="text-slate-700 dark:text-slate-300">
                If you have questions about this Privacy Policy, please contact us through ABA Prep Lab.
              </p>
            </section>
          </div>

          {/* Back Button */}
          <div className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Button
              onClick={() => navigate("/")}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
        <div className="container mx-auto text-center text-slate-500 dark:text-slate-400 text-sm">
          © 2026 ABA Prep Lab. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
